import React from "react";
import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Line,
  Tooltip,
  ReferenceArea,
  Bar,
} from "recharts";
import SgipGraphLegend from "./SgipGraphLegend";
import { Box, Typography } from "@mui/material";
import { formatNumberOrNull } from "../Logic/formatUtils";

interface TimeseriesDataPoint {
  day: string;
  value: number | null;
  previousYearValue?: number | null;
  targetValue?: number;
  weeklyValue?: number;
}

interface Props {
  timeseriesData: TimeseriesDataPoint[] | null;
  targetValueStart?: number;
  targetValueEnd: number;
  topSuccessRegion: boolean;
  flipValues: boolean;
  primaryLabel: string;
  previousYearLabel?: string;
}

const SgipGraph: React.FC<Props> = ({
  timeseriesData,
  targetValueStart = 1,
  targetValueEnd,
  topSuccessRegion,
  flipValues,
  primaryLabel,
  previousYearLabel,
}) => {
  if (!timeseriesData) {
    return null;
  }

  const chartDataDates = timeseriesData?.map((point) => {
    const formattedDate = new Date(point?.day).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const unixDate = new Date(point.day).getTime();

    const targetValue = point.value ? point.value * 1.1 : null;

    return {
      ...point,
      targetValue: targetValue,
      formattedDate,
      date: new Date(point.day),
      unixDate,
    };
  });

  // add target value to the timeseries
  const chartData = chartDataDates.map((point, index) => {
    const slope = (targetValueEnd - targetValueStart) / chartDataDates.length;
    return {
      ...point,
      targetValue: targetValueStart + slope * index,
    };
  });

  const firstDate = chartData[0].unixDate;
  const lastDate = chartData[chartData.length - 1].unixDate;

  const previousYearDataMax = Math.max(...chartData.map((point) => point.previousYearValue || 0));
  const previousYearDataMin = Math.min(...chartData.map((point) => point.previousYearValue || 0));

  const currentYearDataMax = Math.max(...chartData.map((point) => point.value || 0));
  const currentYearDataMin = Math.min(...chartData.map((point) => point.value || 0));

  const existingDataMax = Math.max(previousYearDataMax, currentYearDataMax);
  const existingDataMin = Math.min(previousYearDataMin, currentYearDataMin);

  // relativly how large green success region is rendered
  const successMargin = 1.3;

  // range of green success region, depends on whether we render it on top or bottom
  const successRegionMaxInitial = topSuccessRegion
    ? targetValueEnd * successMargin
    : targetValueEnd;
  const successRegionMinInitial = topSuccessRegion
    ? targetValueEnd
    : targetValueEnd * successMargin;
  //   include space for target rectangle
  const graphMax = Math.max(existingDataMax, targetValueStart, successRegionMaxInitial);
  //   used in the future to render y axis labels
  const tickMax = Math.max(existingDataMax, targetValueStart, targetValueEnd);
  const graphMin = Math.min(
    existingDataMin,
    targetValueStart,
    targetValueEnd,
    successRegionMinInitial,
  );

  const successRegionMax = topSuccessRegion
    ? Math.max(existingDataMax, successRegionMaxInitial)
    : successRegionMaxInitial;
  const successRegionMin = topSuccessRegion
    ? successRegionMinInitial
    : Math.min(existingDataMin, successRegionMinInitial);

  const tickFormatter = (value: number) => {
    if (value !== firstDate && value !== lastDate) {
      return "";
    }
    const date = new Date(value);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!(active && payload && payload.length)) {
      return null;
    }

    const date = new Date(label);
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    const primaryValue = payload.find((p: any) => p.dataKey === "value")?.value;
    const previousYearValue = payload.find((p: any) => p.dataKey === "previousYearValue")?.value;

    return (
      <SgipGraphLegend
        date={formattedDate}
        primaryLabel={primaryLabel}
        primaryValue={formatNumberOrNull(primaryValue)}
        previousYearLabel={"Prior year"}
        previousYearValue={formatNumberOrNull(previousYearValue)}
      />
    );
  };

  return (
    <ResponsiveContainer width="100%" height={200}>
      {/* Can add negative left to compensate for oversized y axis */}
      <ComposedChart data={chartData} margin={{ top: 0, right: 20, left: 20, bottom: 20 }}>
        <CartesianGrid
          strokeDasharray="3 3"
          vertical={false}
          horizontal={false}
          fill="rgba(250, 250, 250, 1)"
        />
        <XAxis
          dataKey="unixDate"
          tick={{ fontSize: 12, fill: "rgba(0, 0, 0, 0.6)", transform: "translate(0, 10)" }}
          tickLine={false}
          axisLine={false}
          tickFormatter={tickFormatter}
          interval={0}
        />
        <YAxis
          tick={{ fontSize: 12, fill: "rgba(0, 0, 0, 0.6)" }}
          ticks={[graphMin, tickMax]}
          tickLine={false}
          axisLine={false}
          domain={["dataMin", graphMax]}
          hide={true}
        />
        {/* main line */}
        <Line
          type="monotone"
          dataKey="value"
          stroke="#000000"
          strokeWidth={2}
          dot={false}
          connectNulls={true}
          isAnimationActive={false}
        />
        {/* Target value line */}
        <Line
          type="monotone"
          dataKey="targetValue"
          stroke="rgba(46, 125, 50, 0.5)"
          strokeWidth={2}
          strokeDasharray="5 5"
          dot={false}
          connectNulls={true}
          isAnimationActive={false}
        />
        {previousYearLabel && (
          <Line
            type="monotone"
            dataKey="previousYearValue"
            stroke="rgba(189, 189, 189, 1)"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            connectNulls={true}
            isAnimationActive={false}
          />
        )}
        <Tooltip
          content={<CustomTooltip />}
          cursor={{ stroke: "#000000", strokeWidth: 1, strokeOpacity: 0.1 }}
        />
        {/* success region */}
        <ReferenceArea
          x1={firstDate}
          x2={lastDate}
          y1={successRegionMin}
          y2={successRegionMax}
          fill="rgba(46, 125, 50, 0.3)"
          ifOverflow="visible"
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default SgipGraph;

// export TimeseriesDataPoint type
export type { TimeseriesDataPoint };
